/**
 * S7 · The catalogue, as the developer portal reads it.
 *
 * Every stat card: what it is, what it reads, what fields it gives, one honest
 * example. `inPlugin: false` means the database has a version the installed
 * plugin no longer ships — the one thing this page exists to make visible, so
 * it is a badge on the row rather than grey text at the end of it.
 */

import { useCallback } from "react";
import { useParams } from "react-router-dom";

import {
  Card as Panel,
  Empty,
  LinkButton,
  PageHead,
  Pill,
  Problem,
  Stat,
  StatGrid,
  Table,
  Td,
  Th,
  Toolbar,
  Stack,
} from "@/components/kit";
import { loadCatalogue, type Card } from "@/lib/statsEngine/api";
import { useApi } from "@/spa/useApi";

export function StatsEngineCataloguePage() {
  const { sport = "" } = useParams<{ sport: string }>();
  const load = useCallback(() => loadCatalogue(sport || undefined), [sport]);
  const { data, loading, error } = useApi<Card[]>(load, [sport], "the admin API is not reachable");

  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;

  const cards = data ?? [];
  const missing = cards.filter((c) => !c.inPlugin).length;
  const editable = cards.filter((c) => c.edit !== "none").length;

  return (
    <Stack>
      <PageHead
        title="Stat catalogue"
        note="Every stat this platform can produce, with the fields it gives and what it reads. This is what a partner developer sees."
        actions={
          <Toolbar>
            <LinkButton to={`/${sport}/stats-engine`}>Stats</LinkButton>
            <LinkButton to={`/${sport}/stats-engine/runs`}>Runs</LinkButton>
          </Toolbar>
        }
      />

      <StatGrid>
        <Stat label="Stats" value={cards.length} note={sport ? `declared for ${sport}` : "across every sport"} />
        <Stat
          label="Editable by hand"
          value={editable}
          note="ops may override a number, with a reason"
        />
        <Stat
          label="Not in the plugin"
          value={missing}
          note={missing ? "the database has a version the code no longer ships" : "every card matches the code"}
        />
      </StatGrid>

      {loading && !data ? <Panel>Reading…</Panel> : null}

      {data ? (
        cards.length === 0 ? (
          <Panel>
            <Empty
              title="Nothing registered yet"
              note="Run `python -m omnium_stats sync` to register what the installed plugin declares."
            />
          </Panel>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Stat</Th>
                <Th num>Version</Th>
                <Th>Status</Th>
                <Th>Counts over</Th>
                <Th>Gives</Th>
                <Th>Reads</Th>
                <Th>Fields it gives</Th>
                <Th>Edits</Th>
              </tr>
            </thead>
            <tbody>
              {data.map((card) => (
                <tr key={`${card.key}:${card.version}`}>
                  <Td strong>
                    {card.name}
                    <div className="muted mono">{card.key}</div>
                    {card.description ? <div className="muted">{card.description}</div> : null}
                  </Td>
                  <Td num quiet>
                    {card.version}
                  </Td>
                  <Td>
                    <Pill tone={card.status === "live" ? "good" : "neutral"} dot>
                      {card.status}
                    </Pill>
                    {!card.inPlugin ? (
                      <div>
                        <Pill tone="bad" dot>
                          not in the installed plugin
                        </Pill>
                      </div>
                    ) : null}
                  </Td>
                  <Td>{card.scopeKind}</Td>
                  <Td>{card.gives}</Td>
                  <Td quiet>{card.reads.join(", ")}</Td>
                  <Td quiet>{Object.keys(card.fields).join(", ")}</Td>
                  <Td quiet>{card.edit}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )
      ) : null}
    </Stack>
  );
}
